import { useEffect, useState } from "react";
import api, { apiErr, fmtMoney, fmtDate } from "@/lib/api";
import { INVOICE_STATUSES } from "@/lib/constants";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { ValBadge } from "@/lib/validation";
import InvoiceDetailDialog from "@/components/InvoiceDetailDialog";
import PdfPreviewDialog from "@/components/PdfPreviewDialog";
import { SendEmailDialog } from "@/components/SendEmailDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, ReceiptText, MoreVertical, Pencil, Trash2, Download, Trash, Loader2, Wallet, Eye, Package, Wrench, Mail } from "lucide-react";
import { toast } from "sonner";

const emptyItem = () => ({ description: "", quantite: 1, unite: "u", prix_unitaire: 0, remise: 0, tva: 18 });

export default function Invoices() {
  const [items, setItems] = useState(null);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [articles, setArticles] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [emailDoc, setEmailDoc] = useState(null);

  const load = () => api.get("/invoices").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/client-projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/catalog/articles").then((r) => setArticles(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data.filter((s) => s.actif !== false))).catch(() => {});
  }, []);

  const openNew = () => { setForm({ client_id: "", client_name: "", client_project_id: "", projet: "", date: new Date().toISOString().slice(0, 10), echeance: "", statut: "Émise", conditions: "", items: [emptyItem()] }); setOpen(true); };
  const openEdit = (inv) => { setForm({ ...inv, items: inv.items?.length ? inv.items : [emptyItem()] }); setOpen(true); };

  const setItem = (i, k, v) => setForm((p) => { const its = [...p.items]; its[i] = { ...its[i], [k]: k === "description" || k === "unite" ? v : Number(v) }; return { ...p, items: its }; });
  const addItem = () => setForm((p) => ({ ...p, items: [...p.items, emptyItem()] }));
  const rmItem = (i) => setForm((p) => ({ ...p, items: p.items.filter((_, j) => j !== i) }));
  const addArticle = (id) => { const a = articles.find((x) => x.id === id); if (!a) return; setForm((p) => ({ ...p, items: [...p.items, { description: a.designation, quantite: 1, unite: a.unite || "u", prix_unitaire: a.prix_vente || 0, remise: 0, tva: 18 }] })); };
  const addService = (id) => { const s = services.find((x) => x.id === id); if (!s) return; setForm((p) => ({ ...p, items: [...p.items, { description: s.nom, quantite: 1, unite: s.unite || "u", prix_unitaire: s.prix || 0, remise: 0, tva: s.tva ?? 18 }] })); };

  const pickClient = (v) => { const c = clients.find((x) => x.id === v); setForm((p) => ({ ...p, client_id: v, client_name: c ? c.nom : p.client_name })); };
  const pickProject = (v) => { const pr = projects.find((x) => x.id === v); setForm((p) => ({ ...p, client_project_id: v, client_project_name: pr ? pr.nom : "", projet: pr ? pr.nom : p.projet })); };

  const totals = () => { let ht = 0, tva = 0; (form?.items || []).forEach((it) => { let b = it.quantite * it.prix_unitaire; b -= b * (it.remise / 100); ht += b; tva += b * (it.tva / 100); }); return { ht, tva, ttc: ht + tva }; };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (form.id) await api.put(`/invoices/${form.id}`, form);
      else await api.post("/invoices", form);
      toast.success("Facture enregistrée"); setOpen(false); load();
    } catch (err) { toast.error(apiErr(err)); } finally { setSaving(false); }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer cette facture ?")) return; await api.delete(`/invoices/${id}`); toast.success("Supprimé"); load(); };

  const t = totals();
  const list = items || [];
  const totalDu = list.reduce((s, i) => s + (i.solde || 0), 0);
  const projectOptions = projects.filter((p) => !form?.client_id || p.client_id === form.client_id);

  return (
    <div>
      <PageHeader title="Factures" subtitle="Facturation, paiements & créances clients">
        <div className="rounded-lg border bg-white px-4 py-2 mr-2 hidden sm:block">
          <p className="text-[10px] uppercase font-mono text-slate-400">Total créances</p>
          <p className="font-mono font-bold text-rose-600">{fmtMoney(totalDu)}</p>
        </div>
        <Button onClick={openNew} data-testid="add-invoice" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouvelle facture</Button>
      </PageHeader>

      {items === null ? <Loader /> : list.length === 0 ? (
        <EmptyState icon={ReceiptText} title="Aucune facture" subtitle="Créez une facture ou transformez un devis accepté." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>N°</TableHead><TableHead>Client</TableHead><TableHead>Date</TableHead><TableHead>Échéance</TableHead>
              <TableHead>Total TTC</TableHead><TableHead>Payé</TableHead><TableHead>Solde</TableHead><TableHead>État</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.map((inv) => (
                <TableRow key={inv.id} className="hover:bg-slate-50 cursor-pointer" data-testid={`invoice-row-${inv.id}`} onClick={() => setDetailId(inv.id)}>
                  <TableCell className="font-mono text-xs text-slate-500">{inv.numero}</TableCell>
                  <TableCell className="font-semibold">{inv.client_name}</TableCell>
                  <TableCell className="text-sm">{fmtDate(inv.date)}</TableCell>
                  <TableCell className="text-sm">{inv.echeance ? fmtDate(inv.echeance) : "—"}</TableCell>
                  <TableCell className="font-mono text-xs font-bold">{fmtMoney(inv.total_ttc)}</TableCell>
                  <TableCell className="font-mono text-xs text-emerald-600">{fmtMoney(inv.montant_paye)}</TableCell>
                  <TableCell className={`font-mono text-xs font-semibold ${inv.solde > 0 ? "text-rose-600" : "text-slate-400"}`}>{fmtMoney(inv.solde)}</TableCell>
                  <TableCell><StatusBadge value={inv.creance_etat} /><ValBadge v={inv.validation} /></TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`invoice-menu-${inv.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setDetailId(inv.id)} data-testid={`invoice-pay-${inv.id}`}><Wallet className="h-4 w-4 mr-2" />Paiements</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setPreview({ path: `/invoices/${inv.id}/pdf`, filename: `${inv.numero}.pdf` })} data-testid={`invoice-preview-${inv.id}`}><Eye className="h-4 w-4 mr-2" />Aperçu avant impression</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setPreview({ path: `/invoices/${inv.id}/pdf`, filename: `${inv.numero}.pdf` })} data-testid={`invoice-pdf-${inv.id}`}><Download className="h-4 w-4 mr-2" />Télécharger PDF</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setEmailDoc(inv)} data-testid={`invoice-email-${inv.id}`}><Mail className="h-4 w-4 mr-2" />Envoyer par e-mail</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEdit(inv)}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => remove(inv.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{form?.id ? "Modifier la facture" : "Nouvelle facture"}</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div><Label className="text-xs">Client</Label>
                  <Select value={form.client_id || ""} onValueChange={pickClient}>
                    <SelectTrigger className="mt-1" data-testid="invoice-client"><SelectValue placeholder={form.client_name || "Sélectionner"} /></SelectTrigger>
                    <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Projet client</Label>
                  <Select value={form.client_project_id || ""} onValueChange={pickProject}>
                    <SelectTrigger className="mt-1" data-testid="invoice-project"><SelectValue placeholder={form.projet || "Sélectionner"} /></SelectTrigger>
                    <SelectContent>{projectOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.nom}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" /></div>
                <div><Label className="text-xs">Échéance</Label><Input type="date" value={form.echeance || ""} onChange={(e) => setForm({ ...form, echeance: e.target.value })} data-testid="invoice-echeance" className="mt-1" /></div>
              </div>

              <div className="rounded-lg border overflow-hidden">
                <div className="overflow-x-auto scrollbar-thin">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500"><tr>
                      <th className="text-left p-2 min-w-48">Description</th><th className="p-2">Qté</th><th className="p-2">Unité</th>
                      <th className="p-2">P.U.</th><th className="p-2">Rem.%</th><th className="p-2">TVA%</th><th className="p-2">Total HT</th><th></th>
                    </tr></thead>
                    <tbody>
                      {form.items.map((it, i) => {
                        let b = it.quantite * it.prix_unitaire; b -= b * (it.remise / 100);
                        return (
                          <tr key={i} className="border-t">
                            <td className="p-1"><Input value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`item-desc-${i}`} className="h-8" /></td>
                            <td className="p-1"><Input type="number" value={it.quantite} onChange={(e) => setItem(i, "quantite", e.target.value)} className="h-8 w-16" /></td>
                            <td className="p-1"><Input value={it.unite} onChange={(e) => setItem(i, "unite", e.target.value)} className="h-8 w-16" /></td>
                            <td className="p-1"><Input type="number" value={it.prix_unitaire} onChange={(e) => setItem(i, "prix_unitaire", e.target.value)} className="h-8 w-28" /></td>
                            <td className="p-1"><Input type="number" value={it.remise} onChange={(e) => setItem(i, "remise", e.target.value)} className="h-8 w-16" /></td>
                            <td className="p-1"><Input type="number" value={it.tva} onChange={(e) => setItem(i, "tva", e.target.value)} className="h-8 w-16" /></td>
                            <td className="p-1 font-mono text-xs text-right whitespace-nowrap">{fmtMoney(b)}</td>
                            <td className="p-1"><Button type="button" variant="ghost" size="icon" onClick={() => rmItem(i)}><Trash className="h-4 w-4 text-rose-500" /></Button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="p-2 border-t bg-slate-50 flex flex-wrap items-center gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-item"><Plus className="h-4 w-4 mr-1" />Ligne libre</Button>
                  <Select value="" onValueChange={addArticle}>
                    <SelectTrigger className="h-9 w-44" data-testid="invoice-add-article"><Package className="h-4 w-4 mr-1 text-slate-400" /><SelectValue placeholder="Ajouter un article" /></SelectTrigger>
                    <SelectContent>{articles.map((a) => <SelectItem key={a.id} value={a.id}>{a.designation}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value="" onValueChange={addService}>
                    <SelectTrigger className="h-9 w-44" data-testid="invoice-add-service"><Wrench className="h-4 w-4 mr-1 text-slate-400" /><SelectValue placeholder="Ajouter une prestation" /></SelectTrigger>
                    <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.nom}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div><Label className="text-xs">Conditions de paiement</Label><Textarea value={form.conditions} onChange={(e) => setForm({ ...form, conditions: e.target.value })} rows={3} className="mt-1" /></div>
                  {form.id && <div><Label className="text-xs">Statut</Label>
                    <Select value={form.statut} onValueChange={(v) => setForm({ ...form, statut: v })}><SelectTrigger className="mt-1" data-testid="invoice-statut"><SelectValue /></SelectTrigger>
                      <SelectContent>{INVOICE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>}
                </div>
                <div className="rounded-lg border bg-slate-50 p-4 space-y-2 self-start">
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Total HT</span><span className="font-mono">{fmtMoney(t.ht)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-500">TVA</span><span className="font-mono">{fmtMoney(t.tva)}</span></div>
                  <div className="flex justify-between text-base font-bold border-t pt-2"><span>Total TTC</span><span className="font-mono text-brand-600">{fmtMoney(t.ttc)}</span></div>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button type="submit" disabled={saving} data-testid="invoice-submit" className="bg-brand-600 hover:bg-brand-700 text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer la facture"}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <InvoiceDetailDialog invoiceId={detailId} onOpenChange={(o) => !o && setDetailId(null)} onChanged={load} />
      <PdfPreviewDialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)} path={preview?.path} filename={preview?.filename} title="Aperçu de la facture" />
      <SendEmailDialog open={!!emailDoc} onOpenChange={(o) => !o && setEmailDoc(null)} endpoint={`/invoices/${emailDoc?.id}/send-email`} docLabel="La facture" numero={emailDoc?.numero} />
    </div>
  );
}
